import React, { useRef, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { trackEvent } from '../../utils/Analytics';
import { FileUploader } from './FileUploader';
import { ResultsDisplay } from './ResultsDisplay';
import { ErrorDisplay } from './ErrorDisplay';
import { ConsentModal } from './ConsentModal';
import { AppHeader } from './AppHeader';
import { fetchWithLanguage } from '../utils/api';
import config from '../config';

export function AppContent() {
  const { t, ready } = useTranslation();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasConsent, setHasConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const fileUploaderRef = useRef(null);
  const resultsRef = useRef(null);

  const handleFileUpload = async (file, directResults, error) => {
    if (file || directResults || error) {
      setResults(null);
      setError(null);
      setLoading(true);
    }

    try {
      let results;
      if (directResults) {
        results = directResults;
      } else if (file) {
        results = await processFile(file);
      } else if (error) {
        throw new Error(error);
      }

      if (results) {
        handleSuccess(results);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const processFile = async (file) => {
    trackEvent('Lab Results', 'Process Start', file.type, file.size);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithLanguage(`${config.apiUrl}/analyze`, {
      method: 'POST',
      body: formData,
    });

    const results = await response.json();

    if (results.error) {
      throw results.error;
    }

    if (!response.ok) {
      throw new Error('Failed to process file');
    }

    return results;
  };

  const handleSuccess = (results) => {
    setResults(results);
    trackEvent('Lab Results', 'Process Success');
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handleError = (err) => {
    console.error('Error processing file:', err);
    setError(err.type ? err : {
      type: 'SYSTEM_ERROR',
      message: err.message || 'Failed to process file'
    });
    trackEvent('Lab Results', 'Process Error', err.message);
  };

  const handleConsent = () => {
    setHasConsent(true);
    setShowConsentModal(false);
    trackEvent('Consent', 'Agreed');
    fileUploaderRef.current?.openFileBrowser();
  };

  const handleUploadAttempt = (file, directResults, error) => {
    if (!file && !directResults && !error) {
      if (!hasConsent) {
        setShowConsentModal(true);
        trackEvent('Consent', 'Prompted');
        return;
      }
      fileUploaderRef.current?.openFileBrowser();
      return;
    }
    handleFileUpload(file, directResults, error);
  };

  if (!ready) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-violet-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto lg:px-8">
        <AppHeader />

        {showConsentModal && (
          <ConsentModal
            onAccept={handleConsent}
            onClose={() => setShowConsentModal(false)}
          />
        )}

        <div className="mt-8">
          <FileUploader
            ref={fileUploaderRef}
            onFileUpload={handleUploadAttempt}
            isError={!!error}
            hasConsent={hasConsent}
            isProcessing={loading}
          />
        </div>

        {error && <div className="mt-8"><ErrorDisplay error={error} /></div>}

        <div ref={resultsRef} className="mt-8">
          {results && <ResultsDisplay results={results} />}
        </div>
      </div>
    </div>
  );
} 